import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Package, MapPin, Calendar, Clock, DollarSign, Truck, User,
  CheckCircle, XCircle, AlertCircle, Loader2, Eye, Navigation, FileText,
  Building2, Phone, Mail, Star, Edit, Trash2, X, Send, MoreVertical, Archive,
  Wallet, CreditCard
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { adminSidebarLinks } from '@/config/adminSidebarLinks';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { adminAPI, vehiclesAPI, api } from '@/services/api';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';
import { useConstantsContext } from '@/contexts/ConstantsContext';
import { constantsUtils } from '@/hooks/useConstants';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const AdminRequestDetails = () => {
  const { clientId, requestId } = useParams();
  const navigate = useNavigate();
  const { constants } = useConstantsContext();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [offerActionDialogOpen, setOfferActionDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [offerAction, setOfferAction] = useState(null); // 'accept' or 'reject'
  const [agreedPrice, setAgreedPrice] = useState(''); // Prix convenu (client/admin)
  const [transporterPrice, setTransporterPrice] = useState(''); // Prix transporteur
  const [deleteOfferDialogOpen, setDeleteOfferDialogOpen] = useState(false);
  const [deletingOffer, setDeletingOffer] = useState(false);
  const [offerDeleteAction, setOfferDeleteAction] = useState(null); // 'archive' or 'delete'
  const [transporterModalOpen, setTransporterModalOpen] = useState(false);
  const [selectedTransporter, setSelectedTransporter] = useState(null);
  const [transporterLoading, setTransporterLoading] = useState(false);
  const [offerDetailsModalOpen, setOfferDetailsModalOpen] = useState(false);
  const [selectedOfferForDetails, setSelectedOfferForDetails] = useState(null);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [requestData, setRequestData] = useState({
    preferred_pickup_date: '',
    preferred_delivery_date: '',
    declared_value: '',
    payment_terms: 'PREPAID',
    urgency_level: 'NORMAL',
    description: '',
  });
  // État pour la gestion de visibilité des offres (obsolète dans le nouveau système)
  const [updatingVisibility, setUpdatingVisibility] = useState({});
  // État pour la gestion de visibilité de la demande
  const [requestVisibilityDialogOpen, setRequestVisibilityDialogOpen] = useState(false);
  const [requestVisibilityData, setRequestVisibilityData] = useState({
    visible_to_transporters: false,
    commission_type: 'PERCENTAGE', // 'PERCENTAGE' ou 'FIXED_AMOUNT'
    commission_percentage: '',
    commission_fixed_amount: '',
  });
  const [defaultCommissionPercentage, setDefaultCommissionPercentage] = useState(null);
  const [updatingRequestVisibility, setUpdatingRequestVisibility] = useState(false);
  // État pour stocker les prix admin locaux (pour l'affichage)
  const [localAdminPrices, setLocalAdminPrices] = useState({});
  // État pour la gestion des paiements
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [createPaymentDialogOpen, setCreatePaymentDialogOpen] = useState(false);
  const [paymentBooking, setPaymentBooking] = useState(null);
  const [paymentData, setPaymentData] = useState({
    method_code: '',
    amount: '',
    phone_number: '',
    account_number: '',
    bank_name: '',
    reference: '',
    proof_file: null,
  });
  const [creatingPayment, setCreatingPayment] = useState(false);

  // Charger le pourcentage de commission par défaut
  useEffect(() => {
    const loadDefaultCommission = async () => {
      try {
        const response = await adminAPI.getAdminSettings();
        setDefaultCommissionPercentage(response.data.default_commission_percentage);
      } catch (err) {
        console.error('Erreur lors du chargement du pourcentage par défaut:', err);
      }
    };
    loadDefaultCommission();
  }, []);

  // Charger la demande
  useEffect(() => {
    const fetchRequest = async () => {
      try {
        setLoading(true);
        const response = await adminAPI.getRequestById(requestId);
        setRequest(response.data);
        setRequestData({
          preferred_pickup_date: response.data.preferred_pickup_date || '',
          preferred_delivery_date: response.data.preferred_delivery_date || '',
          declared_value: response.data.declared_value || '',
          payment_terms: response.data.payment_terms || 'PREPAID',
          urgency_level: response.data.urgency_level || 'NORMAL',
          description: response.data.description || '',
        });
        // Initialiser les données de visibilité
        if (response.data) {
          const commissionType = response.data.commission_type || 'PERCENTAGE';
          setRequestVisibilityData({
            visible_to_transporters: response.data.visible_to_transporters || false,
            commission_type: commissionType,
            commission_percentage: response.data.commission_percentage
              ? response.data.commission_percentage.toString()
              : (defaultCommissionPercentage ? defaultCommissionPercentage.toString() : ''),
            commission_fixed_amount: response.data.commission_fixed_amount
              ? response.data.commission_fixed_amount.toString()
              : '',
          });
        }
      } catch (err) {
        console.error('Erreur lors du chargement de la demande:', err);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger la demande.',
          variant: 'destructive',
        });
        navigate(`/admin/clients`);
      } finally {
        setLoading(false);
      }
    };

    if (requestId) {
      fetchRequest();
    }
  }, [requestId, clientId, navigate, defaultCommissionPercentage]);


  // Charger les offres pour cette demande
  useEffect(() => {
    const fetchOffers = async () => {
      if (!requestId) return;
      try {
        setOffersLoading(true);
        const response = await adminAPI.getClientOffers(clientId, { request: requestId });
        const offersData = response.data.results || response.data || [];

        setOffers(offersData);
        // Initialiser les prix admin locaux
        const initialPrices = {};
        offersData.forEach(offer => {
          if (offer.admin_display_price) {
            initialPrices[offer.id] = offer.admin_display_price;
          }
        });
        setLocalAdminPrices(initialPrices);
      } catch (err) {
        console.error('Erreur lors du chargement des offres:', err);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger les offres.',
          variant: 'destructive',
        });
      } finally {
        setOffersLoading(false);
      }
    };

    if (requestId && clientId) {
      fetchOffers();
    }
  }, [requestId, clientId]);

  // Utilitaires pour les badges
  const getStatusBadge = (status) => {
    const statusConfig = {
      'DRAFT': { label: 'Brouillon', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: FileText },
      'PUBLISHED': { label: 'Publiée', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Send },
      'ASSIGNED': { label: 'Assignée', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
      'IN_TRANSIT': { label: 'En transit', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Navigation },
      'COMPLETED': { label: 'Terminée', color: 'bg-green-600/20 text-green-400 border-green-600/30', icon: CheckCircle },
      'CANCELLED': { label: 'Annulée', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
    };
    const config = statusConfig[status] || statusConfig.DRAFT;
    const Icon = config.icon;
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getUrgencyBadge = (urgency) => {
    const urgencyConfig = {
      'LOW': { label: 'Faible', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
      'NORMAL': { label: 'Normale', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      'HIGH': { label: 'Élevée', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
      'URGENT': { label: 'Urgente', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
    };
    const config = urgencyConfig[urgency] || urgencyConfig.NORMAL;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getPaymentTermsBadge = (terms) => {
    const termsConfig = {
      'PREPAID': { label: 'Prépaiement', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      'COD': { label: 'Paiement à la livraison', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      'POSTPAID': { label: 'Paiement différé', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    };
    const config = termsConfig[terms] || termsConfig.PREPAID;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getOfferStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { label: 'En attente', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
      'ACCEPTED': { label: 'Acceptée', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
      'REJECTED': { label: 'Rejetée', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
      'EXPIRED': { label: 'Expirée', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: AlertCircle },
      'ARCHIVED': { label: 'Archivée', color: 'bg-gray-600/20 text-gray-500 border-gray-600/30', icon: Archive },
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    // Afficher le nombre complet avec séparateurs de milliers
    return `${Number(amount).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} GNF`;
  };

  // Fonction utilitaire pour extraire le nom du transporteur
  const getTransporterName = (transporter) => {
    if (!transporter) {
      return 'N/A';
    }

    // Si transporter est juste un ID (string ou UUID), on ne peut pas extraire le nom
    if (typeof transporter === 'string' || (typeof transporter === 'object' && transporter.id && Object.keys(transporter).length === 1)) {
      return 'N/A';
    }

    // Essayer organisation_name (champ principal du TransporterProfile)
    if (transporter.organisation_name) {
      return transporter.organisation_name;
    }

    // Essayer company_name (pour compatibilité, même si ce champ n'existe pas normalement)
    if (transporter.company_name) {
      return transporter.company_name;
    }

    // Essayer user_detail (structure du serializer)
    const user = transporter.user_detail || transporter.user;
    if (user) {
      const firstName = user.first_name || '';
      const lastName = user.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) {
        return fullName;
      }
      // Si pas de nom complet, essayer l'email
      if (user.email) {
        return user.email;
      }
    }

    // Dernier recours : essayer l'email directement sur le transporteur
    if (transporter.email) {
      return transporter.email;
    }

    return 'N/A';
  };

  // Calculer le pourcentage de différence entre le prix de l'offre et celui de la demande
  const calculatePriceDifference = (offerPrice, requestDeclaredValue) => {
    if (!offerPrice || !requestDeclaredValue || requestDeclaredValue === 0) return null;
    const difference = ((offerPrice - requestDeclaredValue) / requestDeclaredValue) * 100;
    return {
      percentage: Math.abs(difference).toFixed(1),
      isIncrease: difference > 0,
      isDecrease: difference < 0,
      isEqual: difference === 0,
    };
  };

  // Charger les détails du transporteur
  const handleViewTransporter = async (transporterId) => {
    try {
      setTransporterLoading(true);
      setSelectedTransporter(null);
      const response = await adminAPI.getTransporterById(transporterId);
      setSelectedTransporter(response.data);
      setTransporterModalOpen(true);
    } catch (err) {
      console.error('Erreur lors du chargement du transporteur:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les détails du transporteur.',
        variant: 'destructive',
      });
    } finally {
      setTransporterLoading(false);
    }
  };

  // Afficher les détails complets de l'offre
  const handleViewOfferDetails = (offer) => {
    setSelectedOfferForDetails(offer);
    setOfferDetailsModalOpen(true);
  };

  // Charger et afficher les détails du véhicule
  const handleViewVehicle = async (vehicleId) => {
    if (!vehicleId) return;
    try {
      setVehicleLoading(true);
      setSelectedVehicle(null);
      const response = await vehiclesAPI.getVehicleById(vehicleId);
      setSelectedVehicle(response.data);
      setVehicleModalOpen(true);
    } catch (err) {
      console.error('Erreur lors du chargement du véhicule:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les détails du véhicule.',
        variant: 'destructive',
      });
    } finally {
      setVehicleLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handlers
  const handleUpdateRequest = async () => {
    try {
      await adminAPI.updateRequest(requestId, {
        preferred_pickup_date: requestData.preferred_pickup_date || null,
        preferred_delivery_date: requestData.preferred_delivery_date || null,
        declared_value: requestData.declared_value ? parseFloat(requestData.declared_value) : null,
        payment_terms: requestData.payment_terms,
        urgency_level: requestData.urgency_level,
        description: requestData.description || '',
      });
      toast({
        title: 'Succès',
        description: 'La demande a été mise à jour avec succès.',
      });
      setEditDialogOpen(false);
      // Recharger la demande
      const response = await adminAPI.getRequestById(requestId);
      setRequest(response.data);
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Impossible de mettre à jour la demande.',
        variant: 'destructive',
      });
    }
  };

  const handleCancelRequest = async () => {
    try {
      await adminAPI.cancelRequestAsAdmin(requestId);
      toast({
        title: 'Succès',
        description: 'La demande a été annulée avec succès.',
      });
      setCancelDialogOpen(false);
      // Recharger la demande
      const response = await adminAPI.getRequestById(requestId);
      setRequest(response.data);
    } catch (err) {
      console.error('Erreur lors de l\'annulation:', err);
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Impossible d\'annuler la demande.',
        variant: 'destructive',
      });
    }
  };

  const handleOfferAction = async () => {
    if (!selectedOffer || !offerAction) return;

    // Validation des prix si on accepte
    if (offerAction === 'accept') {
      if (!agreedPrice || parseFloat(agreedPrice) <= 0) {
        toast({
          title: 'Erreur',
          description: 'Veuillez saisir un prix convenu valide.',
          variant: 'destructive',
        });
        return;
      }
      if (!transporterPrice || parseFloat(transporterPrice) <= 0) {
        toast({
          title: 'Erreur',
          description: 'Veuillez saisir un prix transporteur valide.',
          variant: 'destructive',
        });
        return;
      }
    }

    try {
      if (offerAction === 'accept') {
        // Envoyer le prix convenu comme admin_display_price lors de l'acceptation
        // Le transporter_price sera géré lors de la création du booking
        await adminAPI.updateOffer(selectedOffer.id, {
          status: 'ACCEPTED',
          admin_display_price: parseFloat(agreedPrice),
          transporter_price: parseFloat(transporterPrice)
        });
        toast({
          title: 'Succès',
          description: 'L\'offre a été acceptée avec succès.',
        });
      } else if (offerAction === 'reject') {
        await adminAPI.forceRejectOffer(selectedOffer.id);
        toast({
          title: 'Succès',
          description: 'L\'offre a été rejetée avec succès.',
        });
      }
      setOfferActionDialogOpen(false);
      setSelectedOffer(null);
      setOfferAction(null);
      setAgreedPrice('');
      setTransporterPrice('');
      // Recharger les offres
      const response = await adminAPI.getClientOffers(clientId, { request: requestId });
      const offersData = response.data.results || response.data || [];
      setOffers(offersData);
      // Mettre à jour les prix admin locaux
      const updatedPrices = {};
      offersData.forEach(offer => {
        if (offer.admin_display_price) {
          updatedPrices[offer.id] = offer.admin_display_price;
        }
      });
      setLocalAdminPrices(prev => ({ ...prev, ...updatedPrices }));
    } catch (err) {
      console.error('Erreur lors de l\'action sur l\'offre:', err);
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || `Impossible de ${offerAction === 'accept' ? 'accepter' : 'rejeter'} l'offre.`,
        variant: 'destructive',
      });
    }
  };

  // Fonction pour sauvegarder la visibilité de la demande
  const handleSaveRequestVisibility = async () => {
    if (!requestId) return;

    // Validation
    if (requestVisibilityData.visible_to_transporters) {
      if (requestVisibilityData.commission_type === 'PERCENTAGE') {
        const percentage = parseFloat(requestVisibilityData.commission_percentage);
        if (isNaN(percentage) || percentage < 0 || percentage > 100) {
          toast({
            title: 'Erreur',
            description: 'Le pourcentage de commission doit être un nombre entre 0 et 100.',
            variant: 'destructive',
          });
          return;
        }
      } else if (requestVisibilityData.commission_type === 'FIXED_AMOUNT') {
        const amount = parseFloat(requestVisibilityData.commission_fixed_amount);
        if (isNaN(amount) || amount < 0) {
          toast({
            title: 'Erreur',
            description: 'Le montant fixe de commission doit être un nombre positif.',
            variant: 'destructive',
          });
          return;
        }
      }
    }

    try {
      setUpdatingRequestVisibility(true);
      const payload = {
        visible_to_transporters: requestVisibilityData.visible_to_transporters,
      };

      if (requestVisibilityData.visible_to_transporters) {
        payload.commission_type = requestVisibilityData.commission_type;
        if (requestVisibilityData.commission_type === 'PERCENTAGE') {
          payload.commission_percentage = parseFloat(requestVisibilityData.commission_percentage);
          payload.commission_fixed_amount = null;
        } else {
          payload.commission_fixed_amount = parseFloat(requestVisibilityData.commission_fixed_amount);
          payload.commission_percentage = null;
        }
      } else {
        // Si la visibilité est désactivée, supprimer les données de commission
        payload.commission_type = null;
        payload.commission_percentage = null;
        payload.commission_fixed_amount = null;
      }

      await adminAPI.updateRequestVisibility(requestId, payload);

      // Recharger la demande pour mettre à jour les données
      const response = await adminAPI.getRequestById(requestId);
      setRequest(response.data);

      const commissionType = response.data.commission_type || 'PERCENTAGE';
      setRequestVisibilityData({
        visible_to_transporters: response.data.visible_to_transporters || false,
        commission_type: commissionType,
        commission_percentage: response.data.commission_percentage
          ? response.data.commission_percentage.toString()
          : (defaultCommissionPercentage ? defaultCommissionPercentage.toString() : ''),
        commission_fixed_amount: response.data.commission_fixed_amount
          ? response.data.commission_fixed_amount.toString()
          : '',
      });

      toast({
        title: 'Succès',
        description: 'La visibilité de la demande a été mise à jour avec succès.',
        variant: 'success',
      });

      setRequestVisibilityDialogOpen(false);
    } catch (err) {
      console.error('Erreur lors de la mise à jour de la visibilité:', err);
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Impossible de mettre à jour la visibilité de la demande.',
        variant: 'destructive',
      });
    } finally {
      setUpdatingRequestVisibility(false);
    }
  };

  // Fonction pour mettre à jour la visibilité d'une offre
  const handleUpdateOfferVisibility = async (offerId, visible, adminDisplayPrice) => {
    // Vérifier si l'offre est acceptée avant de permettre la modification
    const offer = offers.find(o => o.id === offerId);
    if (offer && offer.status === 'ACCEPTED') {
      toast({
        title: 'Erreur',
        description: 'Le prix ne peut plus être modifié une fois l\'offre acceptée.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setUpdatingVisibility(prev => ({ ...prev, [offerId]: true }));
      await adminAPI.updateOfferVisibility(offerId, {
        visible_to_client: visible,
        admin_display_price: adminDisplayPrice ? parseFloat(adminDisplayPrice) : null,
      });

      // Mettre à jour l'offre dans la liste
      setOffers(prevOffers =>
        prevOffers.map(offer =>
          offer.id === offerId
            ? { ...offer, visible_to_client: visible, admin_display_price: adminDisplayPrice ? parseFloat(adminDisplayPrice) : null }
            : offer
        )
      );

      toast({
        title: 'Succès',
        description: 'Visibilité mise à jour avec succès',
      });
    } catch (err) {
      console.error('Erreur lors de la mise à jour de la visibilité:', err);
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Impossible de mettre à jour la visibilité',
        variant: 'destructive',
      });
    } finally {
      setUpdatingVisibility(prev => ({ ...prev, [offerId]: false }));
    }
  };

  // Fonction pour archiver une offre (changement de statut)
  const handleArchiveOffer = async (offerId) => {
    try {
      setDeletingOffer(true);
      // Changer le statut à ARCHIVED au lieu du soft delete
      await adminAPI.updateOffer(offerId, { status: 'ARCHIVED' });
      toast({
        title: 'Succès',
        description: 'L\'offre a été archivée avec succès.',
      });
      setDeleteOfferDialogOpen(false);
      setSelectedOffer(null);
      setOfferDeleteAction(null);
      // Recharger les offres
      const response = await adminAPI.getClientOffers(clientId, { request: requestId });
      const offersData = response.data.results || response.data || [];
      setOffers(offersData);
    } catch (err) {
      console.error('Erreur lors de l\'archivage de l\'offre:', err);
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Impossible d\'archiver l\'offre.',
        variant: 'destructive',
      });
    } finally {
      setDeletingOffer(false);
    }
  };

  // Fonction pour supprimer définitivement une offre (hard delete)
  const handleDeleteOffer = async (offerId) => {
    try {
      setDeletingOffer(true);
      await adminAPI.deleteOffer(offerId);
      toast({
        title: 'Succès',
        description: 'L\'offre a été supprimée définitivement.',
      });
      setDeleteOfferDialogOpen(false);
      setSelectedOffer(null);
      setOfferDeleteAction(null);
      // Recharger les offres
      const response = await adminAPI.getClientOffers(clientId, { request: requestId });
      const offersData = response.data.results || response.data || [];
      setOffers(offersData);
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'offre:', err);
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Impossible de supprimer l\'offre.',
        variant: 'destructive',
      });
    } finally {
      setDeletingOffer(false);
    }
  };

  // Fonction pour afficher les paiements du client
  const handleViewPayments = async (bookingId) => {
    try {
      const response = await adminAPI.getBookingById(bookingId);
      setPaymentBooking(response.data);
      setPaymentDialogOpen(true);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les paiements',
        variant: 'destructive',
      });
    }
  };

  // Fonction pour ouvrir la modale de paiement du transporteur
  const handlePayTransporter = async (booking) => {
    try {
      // Calculer le montant restant à payer au transporteur
      // Pour l'admin, on paie le transporteur avec transporter_price
      const transporterPrice = booking.transporter_price || booking.agreed_price || 0;

      // Récupérer les paiements existants du transporteur (paiements où payee_user est le transporteur)
      const transporter = booking.transporter_detail || booking.transporter;
      if (!transporter || !transporter.user) {
        toast({
          title: 'Erreur',
          description: 'Impossible de déterminer le transporteur.',
          variant: 'destructive',
        });
        return;
      }

      // Charger les détails complets du booking pour avoir les paiements
      const response = await adminAPI.getBookingById(booking.id);
      const fullBooking = response.data;

      // Calculer le montant déjà payé au transporteur
      const transporterPayments = (fullBooking.payments || []).filter(
        payment => payment.payee_user === transporter.user || payment.payee_user?.id === transporter.user
      );
      const totalPaidToTransporter = transporterPayments
        .filter(p => p.status === 'SUCCESS')
        .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

      const remainingAmount = Math.max(0, transporterPrice - totalPaidToTransporter);

      if (remainingAmount <= 0) {
        toast({
          title: 'Info',
          description: 'Le transporteur a déjà été entièrement payé.',
        });
        return;
      }

      setPaymentBooking(fullBooking);
      setPaymentData({
        method_code: '',
        amount: remainingAmount.toString(),
        phone_number: '',
        account_number: '',
        bank_name: '',
        reference: '',
        proof_file: null,
      });
      setCreatePaymentDialogOpen(true);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: 'Impossible d\'initialiser le paiement',
        variant: 'destructive',
      });
    }
  };

  // Fonction pour valider les données de paiement
  const validatePaymentData = () => {
    if (!paymentData.method_code || !paymentData.amount) {
      return { valid: false, message: 'Veuillez sélectionner une méthode de paiement et entrer un montant.' };
    }

    switch (paymentData.method_code) {
      case 'ORANGE_MONEY':
      case 'MTN_MOMO':
        if (!paymentData.phone_number || paymentData.phone_number.length < 9) {
          return { valid: false, message: 'Veuillez entrer un numéro de téléphone valide (9 chiffres).' };
        }
        break;
      case 'BANK_TRANSFER':
        if (!paymentData.bank_name || !paymentData.account_number || !paymentData.reference) {
          return { valid: false, message: 'Veuillez remplir tous les champs du virement bancaire.' };
        }
        break;
      case 'CASH':
        if (!paymentData.proof_file) {
          return { valid: false, message: 'Veuillez télécharger une preuve de paiement pour le paiement en espèces.' };
        }
        break;
      default:
        return { valid: true };
    }

    return { valid: true };
  };

  // Fonction pour créer un paiement au transporteur
  const handleCreatePayment = async () => {
    if (!paymentBooking) {
      toast({
        title: 'Erreur',
        description: 'Aucune réservation sélectionnée.',
        variant: 'destructive',
      });
      return;
    }

    // Valider les données selon la méthode
    const validation = validatePaymentData();
    if (!validation.valid) {
      toast({
        title: 'Erreur',
        description: validation.message,
        variant: 'destructive',
      });
      return;
    }

    try {
      setCreatingPayment(true);

      // Récupérer le transporteur depuis le booking
      const transporter = paymentBooking.transporter_detail || paymentBooking.transporter;
      if (!transporter || !transporter.user) {
        toast({
          title: 'Erreur',
          description: 'Impossible de déterminer le transporteur.',
          variant: 'destructive',
        });
        return;
      }

      const payeeUserId = transporter.user;

      // Créer le paiement
      // Note: payer_user sera automatiquement défini par le backend (l'admin actuel via perform_create)
      const paymentPayload = {
        booking: paymentBooking.id,
        payee_user: payeeUserId, // Le transporteur qui reçoit le paiement (ID utilisateur)
        amount: parseFloat(paymentData.amount),
        currency: 'GNF',
        method_code: paymentData.method_code,
        status: 'PENDING',
      };

      // Si c'est un paiement en espèces, on doit envoyer le fichier de preuve
      if (paymentData.method_code === 'CASH' && paymentData.proof_file) {
        const formData = new FormData();
        Object.keys(paymentPayload).forEach(key => {
          formData.append(key, paymentPayload[key]);
        });
        formData.append('proof', paymentData.proof_file);

        await api.post('/payments/payments/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        await api.post('/payments/payments/', paymentPayload);
      }

      toast({
        title: 'Paiement créé',
        description: 'Le paiement au transporteur a été créé avec succès.',
      });

      // Fermer la modale et réinitialiser les données
      setCreatePaymentDialogOpen(false);
      setPaymentBooking(null);
      setPaymentData({
        method_code: '',
        amount: '',
        phone_number: '',
        account_number: '',
        bank_name: '',
        reference: '',
        proof_file: null,
      });

      // Recharger les offres pour mettre à jour les paiements
      const response = await adminAPI.getClientOffers(clientId, { request: requestId });
      const offersData = response.data.results || response.data || [];
      setOffers(offersData);
    } catch (err) {
      console.error('Erreur lors de la création du paiement:', err);
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Impossible de créer le paiement.',
        variant: 'destructive',
      });
    } finally {
      setCreatingPayment(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout sidebarLinks={adminSidebarLinks}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!request) {
    return (
      <DashboardLayout sidebarLinks={adminSidebarLinks}>
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Demande non trouvée</h3>
            <p className="text-muted-foreground mb-4">La demande demandée n'existe pas ou a été supprimée.</p>
            <Button onClick={() => navigate(`/admin/clients`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à la liste des clients
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>Détails de la demande - Admin - LogiConnect</title>
        <meta name="description" content="Détails complets de la demande de transport" />
      </Helmet>
      <DashboardLayout sidebarLinks={adminSidebarLinks}>
        <div className="space-y-6">
          {/* En-tête */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/clients`)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-white">Détails de la demande</h1>
                <p className="text-muted-foreground mt-1">
                  {request.logiconnect_id && (
                    <LogiConnectIdBadge id={request.logiconnect_id} showLabel={true} />
                  )}
                </p>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <MoreVertical className="w-4 h-4 mr-2" />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </DropdownMenuItem>
                {request.status !== 'CANCELLED' && request.status !== 'COMPLETED' && (
                  <DropdownMenuItem
                    onClick={() => setCancelDialogOpen(true)}
                    className="text-destructive"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Annuler
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate(`/admin/clients`)}>
                  <User className="w-4 h-4 mr-2" />
                  Voir le client
                </DropdownMenuItem>
                {(request.status === 'IN_TRANSIT' || request.status === 'ASSIGNED') && (
                  <DropdownMenuItem onClick={() => navigate(`/admin/tracking?request=${requestId}`)}>
                    <Navigation className="w-4 h-4 mr-2" />
                    Suivre la livraison
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Colonne principale */}
            <div className="lg:col-span-2 space-y-6">
              {/* Informations de la demande */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-primary" />
                    Informations de la demande
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Statut</Label>
                      <div className="mt-1">{getStatusBadge(request.status)}</div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Urgence</Label>
                      <div className="mt-1">{getUrgencyBadge(request.urgency_level)}</div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Mode de paiement</Label>
                      <div className="mt-1">{getPaymentTermsBadge(request.payment_terms)}</div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Valeur déclarée</Label>
                      <div className="mt-1 font-bold text-xl text-secondary">{formatCurrency(request.declared_value)}</div>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Lieu de départ
                      </Label>
                      <p className="mt-1 font-medium">
                        {request.from_location_detail?.city || request.from_location_detail?.label || request.from_location?.city || request.from_location?.label || 'N/A'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {request.from_location_detail?.country || request.from_location?.country || ''}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Lieu d'arrivée
                      </Label>
                      <p className="mt-1 font-medium">
                        {request.to_location_detail?.city || request.to_location_detail?.label || request.to_location?.city || request.to_location?.label || 'N/A'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {request.to_location_detail?.country || request.to_location?.country || ''}
                      </p>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Date de collecte préférée
                      </Label>
                      <p className="mt-1">{formatDate(request.preferred_pickup_date)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Date de livraison préférée
                      </Label>
                      <p className="mt-1">{formatDate(request.preferred_delivery_date)}</p>
                    </div>
                  </div>

                  {request.description && (
                    <>
                      <Separator />
                      <div>
                        <Label className="text-muted-foreground">Description</Label>
                        <p className="mt-1 text-sm">{request.description}</p>
                      </div>
                    </>
                  )}

                  {/* Nouveaux champs métier */}
                  {(request.handling_responsibility || request.goods_type || request.preferred_vehicle_type_detail || request.estimated_weight_kg || request.estimated_volume_m3) && (
                    <>
                      <Separator />
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Informations de transport</h3>
                        <div className="grid grid-cols-2 gap-4">
                          {request.handling_responsibility && (
                            <div>
                              <Label className="text-muted-foreground flex items-center gap-2">
                                <Package className="w-4 h-4" />
                                Manutention
                              </Label>
                              <p className="mt-1 font-medium">
                                {constantsUtils.getLabel(constants?.handling_responsibility, request.handling_responsibility) || request.handling_responsibility}
                              </p>
                            </div>
                          )}
                          {request.goods_type && (
                            <div>
                              <Label className="text-muted-foreground flex items-center gap-2">
                                <Package className="w-4 h-4" />
                                Type de marchandise
                              </Label>
                              <p className="mt-1 font-medium">
                                {constantsUtils.getLabel(constants?.transport_domains, request.goods_type) || request.goods_type}
                              </p>
                            </div>
                          )}
                          {request.preferred_vehicle_type_detail && (
                            <div>
                              <Label className="text-muted-foreground flex items-center gap-2">
                                <Truck className="w-4 h-4" />
                                Type de véhicule souhaité
                              </Label>
                              <p className="mt-1 font-medium">
                                {request.preferred_vehicle_type_detail.label || request.preferred_vehicle_type_detail.code || 'N/A'}
                              </p>
                            </div>
                          )}
                          {request.estimated_weight_kg && (
                            <div>
                              <Label className="text-muted-foreground flex items-center gap-2">
                                <Package className="w-4 h-4" />
                                Poids estimatif
                              </Label>
                              <p className="mt-1 font-medium">{parseFloat(request.estimated_weight_kg).toLocaleString('fr-FR')} kg</p>
                            </div>
                          )}
                          {request.estimated_volume_m3 && (
                            <div>
                              <Label className="text-muted-foreground flex items-center gap-2">
                                <Package className="w-4 h-4" />
                                Volume estimatif
                              </Label>
                              <p className="mt-1 font-medium">{parseFloat(request.estimated_volume_m3).toLocaleString('fr-FR')} m³</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Créée le</Label>
                      <p className="mt-1 text-sm">{formatDate(request.created_at)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Modifiée le</Label>
                      <p className="mt-1 text-sm">{formatDate(request.updated_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Offres reçues - Affichage compact */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Offres reçues ({offers.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {offersLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : offers.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Aucune offre reçue pour cette demande.</p>
                    </div>
                  ) : (
                    <Tabs defaultValue="active" className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="active">
                          Offres actives ({offers.filter(o => o.status !== 'REJECTED' && o.status !== 'ARCHIVED').length})
                        </TabsTrigger>
                        <TabsTrigger value="rejected">
                          Offres rejetées ({offers.filter(o => o.status === 'REJECTED' || o.status === 'ARCHIVED').length})
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="active" className="mt-4">
                        {(() => {
                          // Filtrer les offres actives (non rejetées, non archivées)
                          const activeOffers = offers.filter(offer => offer.status !== 'REJECTED' && offer.status !== 'ARCHIVED');

                          if (activeOffers.length === 0) {
                            return (
                              <div className="text-center py-8 text-muted-foreground">
                                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>Aucune offre active.</p>
                              </div>
                            );
                          }

                          return (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {activeOffers.map((offer) => {
                        const offerRequest = offer.request_detail || offer.request || request;
                        const transporter = offer.transporter_detail || offer.transporter;
                        const transporterId = transporter?.id || (typeof transporter === 'string' ? transporter : offer.transporter);
                        const transporterName = getTransporterName(transporter);
                        const priceDiff = calculatePriceDifference(offer.price, offerRequest?.declared_value);
                        const vehicle = offer.vehicle_detail || offer.vehicle;

                        const isRejected = offer.status === 'REJECTED';
                        const isArchived = offer.status === 'ARCHIVED';

                        return (
                          <Card key={offer.id} className={`bg-card/50 border-border transition-colors ${
                            (isRejected || isArchived)
                              ? 'opacity-50 grayscale hover:opacity-60'
                              : 'hover:border-secondary'
                          }`}>
                            <CardContent className="p-6">
                              <div className="flex flex-col gap-4">
                                {/* En-tête avec statut et prix */}
                                <div className={`space-y-3 pb-3 border-b ${(isRejected || isArchived) ? 'border-gray-500/30' : 'border-border'}`}>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {offer.logiconnect_id && (
                                      <LogiConnectIdBadge id={offer.logiconnect_id} showLabel={false} size="sm" />
                                    )}
                                    {getOfferStatusBadge(offer.status)}
                                    {priceDiff && !priceDiff.isEqual && (
                                      <Badge className={priceDiff.isIncrease ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-green-500/20 text-green-400 border-green-500/30'}>
                                        {priceDiff.isIncrease ? '+' : '-'}{priceDiff.percentage}%
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="space-y-2">
                                    <div className="flex items-baseline gap-3">
                                      <div className="text-2xl font-bold text-secondary">
                                        {formatCurrency(offer.price)}
                                      </div>
                                      <div className="text-sm text-muted-foreground">Prix proposé par le transporteur</div>
                                    </div>
                                    {offer.final_price && offer.final_price !== offer.price && (
                                      <div className="flex items-center gap-2 pt-2 border-t border-border/50">
                                        <div className="text-sm text-muted-foreground">Prix final convenu:</div>
                                        <div className="text-lg font-semibold text-secondary">
                                          {formatCurrency(offer.final_price)}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Truck className="w-4 h-4" />
                                    <span className="truncate font-medium">
                                      {getTransporterName(transporter)}
                                    </span>
                                  </div>
                                </div>

                                {/* Informations du véhicule */}
                                {vehicle && !isRejected && !isArchived && (
                                  <div className="bg-primary-700/30 rounded-lg p-3 border border-primary-700/50">
                                    <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                                      <Truck className="w-4 h-4" />
                                      Véhicule
                                    </h4>
                                    <div className="space-y-2">
                                      {vehicle.plate_number && (
                                        <div className="flex items-center gap-2">
                                          <Truck className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                          <div className="min-w-0 flex-1">
                                            <div className="text-xs text-muted-foreground">Plaque</div>
                                            <div className="text-sm font-semibold text-foreground truncate">
                                              {vehicle.plate_number}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      {vehicle.vehicle_type_label && (
                                        <div className="flex items-center gap-2">
                                          <Package className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                          <div className="min-w-0 flex-1">
                                            <div className="text-xs text-muted-foreground">Type</div>
                                            <div className="text-sm font-semibold text-foreground truncate">
                                              {vehicle.vehicle_type_label}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Commission de la plateforme - Affichage pour l'admin */}
                                {!isRejected && !isArchived && offerRequest && (() => {
                                  const offerPrice = parseFloat(offer.price) || 0;
                                  let commission = 0;

                                  if (offerRequest.commission_type === 'PERCENTAGE' && offerRequest.commission_percentage) {
                                    commission = offerPrice * (offerRequest.commission_percentage / 100);
                                  } else if (offerRequest.commission_type === 'FIXED_AMOUNT' && offerRequest.commission_fixed_amount) {
                                    commission = parseFloat(offerRequest.commission_fixed_amount);
                                  }

                                  const transporterAmount = offerPrice - commission;

                                  return commission > 0 ? (
                                    <div className="bg-purple-500/10 rounded-lg p-3 border-2 border-purple-500/30">
                                      <div className="flex items-center justify-between mb-2">
                                        <Label className="text-sm font-semibold text-purple-400 flex items-center gap-2">
                                          <Wallet className="w-4 h-4" />
                                          Commission plateforme
                                        </Label>
                                        <span className="text-lg font-bold text-purple-400">
                                          {formatCurrency(commission)}
                                        </span>
                                      </div>
                                      <div className="space-y-1 text-xs text-purple-300/70">
                                        <div className="flex justify-between">
                                          <span>Prix proposé:</span>
                                          <span className="font-semibold">{formatCurrency(offerPrice)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>Commission ({offerRequest.commission_type === 'PERCENTAGE' ? `${offerRequest.commission_percentage}%` : 'fixe'}):</span>
                                          <span className="font-semibold text-purple-400">-{formatCurrency(commission)}</span>
                                        </div>
                                        <div className="flex justify-between pt-1 border-t border-purple-500/30">
                                          <span className="font-semibold">Montant transporteur:</span>
                                          <span className="font-bold text-green-400">{formatCurrency(transporterAmount)}</span>
                                        </div>
                                      </div>
                                    </div>
                                  ) : null;
                                })()}

                                {/* Actions rapides */}
                                <div className={`flex items-center gap-2 pt-3 border-t ${(isRejected || isArchived) ? 'border-gray-500/30' : 'border-border'}`}>
                                  {isRejected ? (
                                    <>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          setSelectedOffer(offer);
                                          setOfferDeleteAction('archive');
                                          setDeleteOfferDialogOpen(true);
                                        }}
                                        className="text-sm h-8 flex-1"
                                        disabled={deletingOffer}
                                      >
                                        <Archive className="w-4 h-4 mr-1" />
                                        Archiver
                                      </Button>
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() => {
                                          setSelectedOffer(offer);
                                          setOfferDeleteAction('delete');
                                          setDeleteOfferDialogOpen(true);
                                        }}
                                        className="text-sm h-8 flex-1"
                                        disabled={deletingOffer}
                                      >
                                        <Trash2 className="w-4 h-4 mr-1" />
                                        Supprimer
                                      </Button>
                                    </>
                                  ) : isArchived ? (
                                    <>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          setSelectedOffer(offer);
                                          setOfferDeleteAction('delete');
                                          setDeleteOfferDialogOpen(true);
                                        }}
                                        className="text-sm h-8 flex-1"
                                        disabled={deletingOffer}
                                      >
                                        <Trash2 className="w-4 h-4 mr-1" />
                                        Supprimer définitivement
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleViewOfferDetails(offer)}
                                        className="text-sm h-8 flex-1"
                                      >
                                        <Eye className="w-4 h-4 mr-1" />
                                        Détails
                                      </Button>
                                      {offer.status === 'PENDING' && (
                                        <>
                                          <Button
                                            variant="default"
                                            size="sm"
                                            onClick={() => {
                                              setSelectedOffer(offer);
                                              setOfferAction('accept');
                                              // Initialiser le prix convenu avec le prix admin ou le prix de l'offre
                                              setAgreedPrice(offer.admin_display_price || offer.price || '');
                                              // Initialiser le prix transporteur avec le prix original de l'offre
                                              setTransporterPrice(offer.price || '');
                                              setOfferActionDialogOpen(true);
                                            }}
                                            className="text-sm h-8"
                                          >
                                            <CheckCircle className="w-4 h-4 mr-1" />
                                            Accepter
                                          </Button>
                                          <Button
                                            variant="destructive"
                                            size="sm"
                                            onClick={() => {
                                              setSelectedOffer(offer);
                                              setOfferAction('reject');
                                              setOfferActionDialogOpen(true);
                                            }}
                                            className="text-sm h-8"
                                          >
                                            <XCircle className="w-4 h-4 mr-1" />
                                            Rejeter
                                          </Button>
                                        </>
                                      )}
                                    </>
                                  )}
                                </div>

                                {/* Section Mission (si offre acceptée) */}
                                {offer.status === 'ACCEPTED' && offer.booking_detail && (() => {
                                  const booking = offer.booking_detail;
                                  const getBookingStatusBadge = (status) => {
                                    const statusConfig = {
                                      CONFIRMED: { label: 'Confirmée', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: CheckCircle },
                                      IN_PROGRESS: { label: 'En cours', className: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: Clock },
                                      DELIVERED: { label: 'Livrée', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Package },
                                      COMPLETED: { label: 'Terminée', className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
                                      CANCELLED: { label: 'Annulée', className: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
                                    };
                                    const config = statusConfig[status] || statusConfig.CONFIRMED;
                                    const Icon = config.icon;
                                    return (
                                      <Badge className={config.className}>
                                        <Icon className="w-3 h-3 mr-1" />
                                        {config.label}
                                      </Badge>
                                    );
                                  };

                                  // Calculer les paiements du client (paiements où payer_user est le client)
                                  const clientPayments = (booking.payments || []).filter(
                                    payment => {
                                      const client = booking.client_detail || booking.client;
                                      const clientUserId = client?.user || client?.user_detail?.id;
                                      return payment.payer_user === clientUserId || payment.payer_user?.id === clientUserId;
                                    }
                                  );
                                  const clientPaymentsCount = clientPayments.length;
                                  const clientTotalPaid = clientPayments
                                    .filter(p => p.status === 'SUCCESS')
                                    .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

                                  // Calculer les paiements au transporteur
                                  const transporter = booking.transporter_detail || booking.transporter;
                                  const transporterUserId = transporter?.user;
                                  const transporterPayments = (booking.payments || []).filter(
                                    payment => payment.payee_user === transporterUserId || payment.payee_user?.id === transporterUserId
                                  );
                                  const transporterTotalPaid = transporterPayments
                                    .filter(p => p.status === 'SUCCESS')
                                    .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                                  const transporterPrice = booking.transporter_price || booking.agreed_price || 0;
                                  const transporterRemaining = Math.max(0, transporterPrice - transporterTotalPaid);

                                  return (
                                    <div className="mt-4 pt-4 border-t border-border bg-primary-700/20 rounded-lg p-3">
                                      <div className="flex items-center gap-2 mb-3">
                                        <Package className="w-4 h-4 text-primary" />
                                        <h4 className="text-sm font-semibold">Mission créée</h4>
                                      </div>
                                      <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div>
                                          <Label className="text-xs text-muted-foreground">Statut</Label>
                                          <div className="mt-1">
                                            {getBookingStatusBadge(booking.status_code)}
                                          </div>
                                        </div>
                                        <div>
                                          <Label className="text-xs text-muted-foreground">Paiement client</Label>
                                          <div className="mt-1">
                                            {booking.payment_percentage !== undefined ? (
                                              <Badge className={
                                                booking.payment_percentage >= 100 ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                                                booking.payment_percentage > 0 ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                                                'bg-red-500/20 text-red-400 border-red-500/30'
                                              }>
                                                {booking.payment_percentage >= 100 ? 'Payé' : booking.payment_percentage > 0 ? `Partiel (${booking.payment_percentage}%)` : 'Non payé'}
                                              </Badge>
                                            ) : (
                                              <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">N/A</Badge>
                                            )}
                                          </div>
                                        </div>
                                        <div>
                                          <Label className="text-xs text-muted-foreground">Prix convenu</Label>
                                          <p className="text-sm font-semibold text-primary mt-1">
                                            {formatCurrency(booking.agreed_price)}
                                          </p>
                                        </div>
                                        {booking.transporter_price && booking.transporter_price !== booking.agreed_price && (
                                          <div>
                                            <Label className="text-xs text-muted-foreground">Prix transporteur</Label>
                                            <p className="text-sm font-semibold text-secondary mt-1">
                                              {formatCurrency(booking.transporter_price)}
                                            </p>
                                          </div>
                                        )}
                                        {booking.payment_percentage !== undefined && booking.payment_percentage < 100 && (
                                          <div className="col-span-2">
                                            <Label className="text-xs text-muted-foreground">Progression paiement client</Label>
                                            <div className="mt-1">
                                              <div className="w-full bg-muted rounded-full h-2 overflow-hidden mb-1">
                                                <div
                                                  className={`h-full rounded-full transition-all ${
                                                    booking.payment_percentage >= 100 ? 'bg-green-500' :
                                                    booking.payment_percentage > 0 ? 'bg-yellow-500' : 'bg-red-500'
                                                  }`}
                                                  style={{ width: `${Math.min(booking.payment_percentage, 100)}%` }}
                                                />
                                              </div>
                                              <p className="text-xs text-muted-foreground">
                                                {formatCurrency(booking.total_paid || 0)} / {formatCurrency(booking.agreed_price)}
                                              </p>
                                            </div>
                                          </div>
                                        )}
                                      </div>

                                      {/* Section Paiements */}
                                      <div className="mt-4 pt-4 border-t border-border space-y-3">
                                        {/* Paiements du client */}
                                        <div>
                                          <div className="flex items-center justify-between mb-2">
                                            <Label className="text-xs font-semibold text-muted-foreground">Paiements du client</Label>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => handleViewPayments(booking.id)}
                                              className="h-7 text-xs"
                                            >
                                              <CreditCard className="w-3 h-3 mr-1" />
                                              Voir ({clientPaymentsCount})
                                            </Button>
                                          </div>
                                          <div className="text-xs text-muted-foreground">
                                            Total payé: <span className="font-semibold text-foreground">{formatCurrency(clientTotalPaid)}</span>
                                          </div>
                                        </div>

                                        {/* Paiement du transporteur */}
                                        <div>
                                          <div className="flex items-center justify-between mb-2">
                                            <Label className="text-xs font-semibold text-muted-foreground">Paiement transporteur</Label>
                                            {transporterRemaining > 0 && (
                                              <Button
                                                variant="default"
                                                size="sm"
                                                onClick={() => handlePayTransporter(booking)}
                                                className="h-7 text-xs bg-green-600 hover:bg-green-700"
                                              >
                                                <Wallet className="w-3 h-3 mr-1" />
                                                Payer
                                              </Button>
                                            )}
                                          </div>
                                          <div className="text-xs text-muted-foreground">
                                            Payé: <span className="font-semibold text-foreground">{formatCurrency(transporterTotalPaid)}</span>
                                            {transporterRemaining > 0 && (
                                              <> / <span className="font-semibold text-secondary">{formatCurrency(transporterPrice)}</span></>
                                            )}
                                          </div>
                                          {transporterRemaining > 0 && (
                                            <div className="text-xs text-muted-foreground mt-1">
                                              Restant: <span className="font-semibold text-orange-500">{formatCurrency(transporterRemaining)}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                            </div>
                          );
                        })()}
                      </TabsContent>

                      <TabsContent value="rejected" className="mt-4">
                        {(() => {
                          // Filtrer les offres rejetées ou archivées
                          const rejectedOffers = offers.filter(offer => offer.status === 'REJECTED' || offer.status === 'ARCHIVED');

                          if (rejectedOffers.length === 0) {
                            return (
                              <div className="text-center py-8 text-muted-foreground">
                                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>Aucune offre rejetée.</p>
                              </div>
                            );
                          }

                          return (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {rejectedOffers.map((offer) => {
                                const offerRequest = offer.request_detail || offer.request || request;
                                const transporter = offer.transporter_detail || offer.transporter;
                                const transporterName = getTransporterName(transporter);
                                const priceDiff = calculatePriceDifference(offer.price, offerRequest?.declared_value);
                                const isRejected = offer.status === 'REJECTED';
                                const isArchived = offer.status === 'ARCHIVED';

                                return (
                                  <Card key={offer.id} className={`bg-card/50 border-border transition-colors opacity-50 grayscale hover:opacity-60`}>
                                    <CardContent className="p-6">
                                      <div className="flex flex-col gap-4">
                                        {/* En-tête avec statut et prix */}
                                        <div className="space-y-3 pb-3 border-b border-gray-500/30">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            {offer.logiconnect_id && (
                                              <LogiConnectIdBadge id={offer.logiconnect_id} showLabel={false} size="sm" />
                                            )}
                                            {getOfferStatusBadge(offer.status)}
                                            {priceDiff && !priceDiff.isEqual && (
                                              <Badge className={priceDiff.isIncrease ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-green-500/20 text-green-400 border-green-500/30'}>
                                                {priceDiff.isIncrease ? '+' : '-'}{priceDiff.percentage}%
                                              </Badge>
                                            )}
                                          </div>
                                          <div className="space-y-2">
                                            <div className="flex items-baseline gap-3">
                                              <div className="text-2xl font-bold text-secondary">
                                                {formatCurrency(offer.price)}
                                              </div>
                                              <div className="text-sm text-muted-foreground">Prix transporteur</div>
                                            </div>
                                            {offer.admin_display_price && (
                                              <div className="flex items-center gap-2 pt-2 border-t border-border/50">
                                                <div className="text-sm text-muted-foreground">Prix administrateur:</div>
                                                <div className="text-lg font-semibold text-secondary">
                                                  {formatCurrency(offer.admin_display_price)}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Truck className="w-4 h-4" />
                                            <span className="truncate font-medium">
                                              {transporterName}
                                            </span>
                                          </div>
                                        </div>

                                        {/* Actions rapides */}
                                        <div className="flex items-center gap-2 pt-3 border-t border-gray-500/30">
                                          {isRejected ? (
                                            <>
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                  setSelectedOffer(offer);
                                                  setOfferDeleteAction('archive');
                                                  setDeleteOfferDialogOpen(true);
                                                }}
                                                className="text-sm h-8 flex-1"
                                                disabled={deletingOffer}
                                              >
                                                <Archive className="w-4 h-4 mr-1" />
                                                Archiver
                                              </Button>
                                              <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => {
                                                  setSelectedOffer(offer);
                                                  setOfferDeleteAction('delete');
                                                  setDeleteOfferDialogOpen(true);
                                                }}
                                                className="text-sm h-8 flex-1"
                                                disabled={deletingOffer}
                                              >
                                                <Trash2 className="w-4 h-4 mr-1" />
                                                Supprimer
                                              </Button>
                                            </>
                                          ) : isArchived ? (
                                            <>
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleViewOfferDetails(offer)}
                                                className="text-sm h-8 flex-1"
                                              >
                                                <Eye className="w-4 h-4 mr-1" />
                                                Détails
                                              </Button>
                                              <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => {
                                                  setSelectedOffer(offer);
                                                  setOfferDeleteAction('delete');
                                                  setDeleteOfferDialogOpen(true);
                                                }}
                                                className="text-sm h-8 flex-1"
                                                disabled={deletingOffer}
                                              >
                                                <Trash2 className="w-4 h-4 mr-1" />
                                                Supprimer définitivement
                                              </Button>
                                            </>
                                          ) : (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => handleViewOfferDetails(offer)}
                                              className="text-sm h-8 flex-1"
                                            >
                                              <Eye className="w-4 h-4 mr-1" />
                                              Détails
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </TabsContent>
                    </Tabs>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Colonne latérale */}
            <div className="space-y-6">
              {/* Informations client */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" />
                    Client
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(() => {
                    const client = request.client_detail || request.client;
                    const clientUser = client?.user || request.client?.user;
                    return client ? (
                      <>
                        {client.logiconnect_id && (
                          <div>
                            <Label className="text-muted-foreground">ID LogiConnect</Label>
                            <div className="mt-1">
                              <LogiConnectIdBadge id={client.logiconnect_id} showLabel={true} size="sm" />
                            </div>
                          </div>
                        )}
                        <div>
                          <Label className="text-muted-foreground">Nom</Label>
                          <p className="font-semibold">
                            {client.company_name || `${clientUser?.first_name || ''} ${clientUser?.last_name || ''}`.trim() || 'N/A'}
                          </p>
                          {client.company_name && (clientUser?.first_name || clientUser?.last_name) && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {clientUser?.first_name} {clientUser?.last_name}
                            </p>
                          )}
                        </div>
                        {client.type_client && (
                          <div>
                            <Label className="text-muted-foreground">Type de client</Label>
                            <p className="text-sm font-medium">
                              {constantsUtils.getLabel(constants?.client_type, client.type_client) || client.type_client}
                            </p>
                          </div>
                        )}
                        <div>
                          <Label className="text-muted-foreground flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            Email
                          </Label>
                          <p className="text-sm">{clientUser?.email || 'N/A'}</p>
                        </div>
                        {clientUser?.phone_number && (
                          <div>
                            <Label className="text-muted-foreground flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              Téléphone
                            </Label>
                            <p className="text-sm">{clientUser.phone_number}</p>
                          </div>
                        )}
                        {client.address && (
                          <div>
                            <Label className="text-muted-foreground flex items-center gap-2">
                              <Building2 className="w-4 h-4" />
                              Adresse
                            </Label>
                            <p className="text-sm">{client.address}</p>
                          </div>
                        )}
                        {client.domaine_activites && Array.isArray(client.domaine_activites) && client.domaine_activites.length > 0 && (
                          <div>
                            <Label className="text-muted-foreground">Domaines d'activité</Label>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {client.domaine_activites.map((domain, index) => (
                                <Badge key={index} variant="outline" className="text-xs">
                                  {constantsUtils.getLabel(constants?.client_domains, domain) || domain}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        <Separator />
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => navigate(`/admin/clients`)}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Voir le profil complet
                        </Button>
                      </>
                    ) : null;
                  })()}
                </CardContent>
              </Card>

              {/* Gestion de visibilité de la demande */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-primary" />
                    Visibilité transporteurs
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-semibold">Visibilité aux transporteurs</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        {request?.visible_to_transporters
                          ? 'Cette demande est visible par les transporteurs'
                          : 'Cette demande n\'est pas visible par les transporteurs'}
                      </p>
                    </div>
                    <Badge variant={request?.visible_to_transporters ? "default" : "secondary"}>
                      {request?.visible_to_transporters ? 'Visible' : 'Masquée'}
                    </Badge>
                  </div>
                  {request?.admin_proposed_price && (
                    <div>
                      <Label className="text-muted-foreground">Prix proposé par l'admin</Label>
                      <p className="text-sm font-semibold text-secondary">{formatCurrency(request.admin_proposed_price)}</p>
                    </div>
                  )}
                  <Separator />
                  <Button
                    onClick={() => setRequestVisibilityDialogOpen(true)}
                    variant="outline"
                    className="w-full"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Gérer la visibilité
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Dialog de modification */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Modifier la demande</DialogTitle>
              <DialogDescription>
                Modifiez les informations de la demande de transport.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date de collecte préférée</Label>
                  <Input
                    type="datetime-local"
                    value={requestData.preferred_pickup_date ? new Date(requestData.preferred_pickup_date).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setRequestData(prev => ({ ...prev, preferred_pickup_date: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Date de livraison préférée</Label>
                  <Input
                    type="datetime-local"
                    value={requestData.preferred_delivery_date ? new Date(requestData.preferred_delivery_date).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setRequestData(prev => ({ ...prev, preferred_delivery_date: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Valeur déclarée (GNF)</Label>
                <Input
                  type="number"
                  value={requestData.declared_value}
                  onChange={(e) => setRequestData(prev => ({ ...prev, declared_value: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Mode de paiement</Label>
                  <Select value={requestData.payment_terms} onValueChange={(value) => setRequestData(prev => ({ ...prev, payment_terms: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PREPAID">Prépaiement</SelectItem>
                      <SelectItem value="COD">Paiement à la livraison</SelectItem>
                      <SelectItem value="POSTPAID">Paiement différé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Niveau d'urgence</Label>
                  <Select value={requestData.urgency_level} onValueChange={(value) => setRequestData(prev => ({ ...prev, urgency_level: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOW">Faible</SelectItem>
                      <SelectItem value="NORMAL">Normale</SelectItem>
                      <SelectItem value="HIGH">Élevée</SelectItem>
                      <SelectItem value="URGENT">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={requestData.description}
                  onChange={(e) => setRequestData(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleUpdateRequest}>
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de confirmation d'annulation */}
        <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Annuler la demande</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir annuler cette demande ? Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Non</AlertDialogCancel>
              <AlertDialogAction onClick={handleCancelRequest} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Oui, annuler
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Dialog de confirmation d'archivage/suppression d'offre */}
        <AlertDialog open={deleteOfferDialogOpen} onOpenChange={(open) => {
          setDeleteOfferDialogOpen(open);
          if (!open) {
            setSelectedOffer(null);
            setOfferDeleteAction(null);
          }
        }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {offerDeleteAction === 'archive' ? 'Archiver l\'offre' : 'Supprimer définitivement l\'offre'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {offerDeleteAction === 'archive'
                  ? 'Êtes-vous sûr de vouloir archiver cette offre ? Elle sera masquée mais pourra être restaurée ultérieurement.'
                  : 'Êtes-vous sûr de vouloir supprimer définitivement cette offre ? Cette action est irréversible et ne peut pas être annulée.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingOffer}>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (offerDeleteAction === 'archive') {
                    handleArchiveOffer(selectedOffer.id);
                  } else if (offerDeleteAction === 'delete') {
                    handleDeleteOffer(selectedOffer.id);
                  }
                }}
                disabled={deletingOffer}
                className={offerDeleteAction === 'delete' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
              >
                {deletingOffer ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {offerDeleteAction === 'archive' ? 'Archivage...' : 'Suppression...'}
                  </>
                ) : (
                  offerDeleteAction === 'archive' ? 'Archiver' : 'Supprimer définitivement'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Dialog de confirmation d'action sur offre */}
        <AlertDialog open={offerActionDialogOpen} onOpenChange={(open) => {
          setOfferActionDialogOpen(open);
          if (!open) {
            setAgreedPrice('');
            setTransporterPrice('');
          }
        }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {offerAction === 'accept' ? 'Forcer l\'acceptation de l\'offre' : 'Forcer le rejet de l\'offre'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir {offerAction === 'accept' ? 'accepter' : 'rejeter'} cette offre ?
                {offerAction === 'accept' && ' Cela créera automatiquement une réservation.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {offerAction === 'accept' && selectedOffer && (
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="agreedPrice">Prix convenu - Client/Admin (GNF) *</Label>
                  <Input
                    id="agreedPrice"
                    type="number"
                    placeholder="Prix que le client et l'admin verront"
                    value={agreedPrice}
                    onChange={(e) => setAgreedPrice(e.target.value)}
                    className="mt-2"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Ce prix sera visible uniquement par le client et l'admin.
                  </p>
                </div>
                <div>
                  <Label htmlFor="transporterPrice">Prix transporteur (GNF) *</Label>
                  <Input
                    id="transporterPrice"
                    type="number"
                    placeholder="Prix que le transporteur verra"
                    value={transporterPrice}
                    onChange={(e) => setTransporterPrice(e.target.value)}
                    className="mt-2"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Prix proposé par le transporteur: {formatCurrency(selectedOffer.price || 0)}
                    {selectedOffer.admin_display_price && selectedOffer.admin_display_price !== selectedOffer.price && (
                      <span className="ml-2">| Prix admin affiché: {formatCurrency(selectedOffer.admin_display_price)}</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ce prix sera visible uniquement par le transporteur dans ses missions.
                  </p>
                </div>
              </div>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => {
                setAgreedPrice('');
                setTransporterPrice('');
              }}>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleOfferAction}
                disabled={offerAction === 'accept' && ((!agreedPrice || parseFloat(agreedPrice) <= 0) || (!transporterPrice || parseFloat(transporterPrice) <= 0))}
              >
                {offerAction === 'accept' ? 'Accepter' : 'Rejeter'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Modale pour les détails du transporteur */}
        <Dialog open={transporterModalOpen} onOpenChange={setTransporterModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-primary" />
                Profil du transporteur
              </DialogTitle>
              <DialogDescription>
                Informations complètes du transporteur qui a fait cette offre
              </DialogDescription>
            </DialogHeader>
            {transporterLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : selectedTransporter ? (
              <div className="space-y-6">
                {/* Informations principales */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Informations générales</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Nom de l'organisation</Label>
                        <p className="font-semibold">{selectedTransporter.profile_data?.organisation_name || 'N/A'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Statut de vérification</Label>
                        <div className="mt-1">
                          {selectedTransporter.profile_data?.verification_status === 'VERIFIED' ? (
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Vérifié
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              En attente
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-muted-foreground flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email
                        </Label>
                        <p className="text-sm">{selectedTransporter.email || 'N/A'}</p>
                      </div>
                      {selectedTransporter.phone_number && (
                        <div>
                          <Label className="text-muted-foreground flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            Téléphone
                          </Label>
                          <p className="text-sm">{selectedTransporter.phone_number}</p>
                        </div>
                      )}
                      {selectedTransporter.profile_data?.rating_avg && (
                        <div>
                          <Label className="text-muted-foreground flex items-center gap-2">
                            <Star className="w-4 h-4" />
                            Note moyenne
                          </Label>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="font-semibold">{selectedTransporter.profile_data.rating_avg}</p>
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Informations supplémentaires si disponibles */}
                {selectedTransporter.profile_data?.registration_number && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Informations légales</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <Label className="text-muted-foreground">Numéro d'enregistrement</Label>
                        <p className="text-sm">{selectedTransporter.profile_data.registration_number}</p>
                      </div>
                      {selectedTransporter.profile_data.tax_info && (
                        <div>
                          <Label className="text-muted-foreground">Informations fiscales</Label>
                          <p className="text-sm">{selectedTransporter.profile_data.tax_info}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Impossible de charger les détails du transporteur.</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modale pour les détails du véhicule */}
        <Dialog open={vehicleModalOpen} onOpenChange={setVehicleModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-primary" />
                Détails du véhicule
              </DialogTitle>
              <DialogDescription>
                Informations complètes du véhicule assigné à cette offre
              </DialogDescription>
            </DialogHeader>
            {vehicleLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : selectedVehicle ? (
              <div className="space-y-6">
                {/* Informations principales */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Informations générales</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Numéro de plaque</Label>
                        <p className="font-semibold">{selectedVehicle.plate_number || 'N/A'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Type de véhicule</Label>
                        <p className="font-semibold">{selectedVehicle.vehicle_type_label || selectedVehicle.vehicle_type?.label || 'N/A'}</p>
                      </div>
                      {selectedVehicle.make && (
                        <div>
                          <Label className="text-muted-foreground">Marque</Label>
                          <p className="text-sm">{selectedVehicle.make}</p>
                        </div>
                      )}
                      {selectedVehicle.model && (
                        <div>
                          <Label className="text-muted-foreground">Modèle</Label>
                          <p className="text-sm">{selectedVehicle.model}</p>
                        </div>
                      )}
                      {selectedVehicle.year && (
                        <div>
                          <Label className="text-muted-foreground">Année</Label>
                          <p className="text-sm">{selectedVehicle.year}</p>
                        </div>
                      )}
                      <div>
                        <Label className="text-muted-foreground">Statut</Label>
                        <div className="mt-1">
                          <Badge className={selectedVehicle.status === 'AVAILABLE' ? 'bg-green-500/20 text-green-400 border-green-500/30' : selectedVehicle.status === 'IN_USE' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'}>
                            {selectedVehicle.status_display || selectedVehicle.status || 'N/A'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Capacités */}
                {(selectedVehicle.capacity_volume || selectedVehicle.capacity_weight) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Capacités</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        {selectedVehicle.capacity_volume && (
                          <div>
                            <Label className="text-muted-foreground">Capacité volumique</Label>
                            <p className="font-semibold text-lg">{selectedVehicle.capacity_volume} m³</p>
                          </div>
                        )}
                        {selectedVehicle.capacity_weight && (
                          <div>
                            <Label className="text-muted-foreground">Capacité de poids</Label>
                            <p className="font-semibold text-lg">{selectedVehicle.capacity_weight} kg</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Documents et validités */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Documents et validités</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {selectedVehicle.insurance_valid_until && (
                        <div>
                          <Label className="text-muted-foreground">Assurance valide jusqu'au</Label>
                          <p className="text-sm mt-1">{formatDate(selectedVehicle.insurance_valid_until)}</p>
                          {selectedVehicle.insurance_status && (
                            <div className="mt-1">
                              <Badge className={
                                selectedVehicle.insurance_status.status === 'valid' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                                selectedVehicle.insurance_status.status === 'expiring_soon' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                                selectedVehicle.insurance_status.status === 'expired' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                'bg-gray-500/20 text-gray-400 border-gray-500/30'
                              }>
                                {typeof selectedVehicle.insurance_status === 'string' ? selectedVehicle.insurance_status : selectedVehicle.insurance_status.message || 'N/A'}
                              </Badge>
                            </div>
                          )}
                        </div>
                      )}
                      {selectedVehicle.technical_control_until && (
                        <div>
                          <Label className="text-muted-foreground">Contrôle technique valide jusqu'au</Label>
                          <p className="text-sm mt-1">{formatDate(selectedVehicle.technical_control_until)}</p>
                          {selectedVehicle.technical_control_status && (
                            <div className="mt-1">
                              <Badge className={
                                selectedVehicle.technical_control_status.status === 'valid' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                                selectedVehicle.technical_control_status.status === 'expiring_soon' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                                selectedVehicle.technical_control_status.status === 'expired' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                'bg-gray-500/20 text-gray-400 border-gray-500/30'
                              }>
                                {typeof selectedVehicle.technical_control_status === 'string' ? selectedVehicle.technical_control_status : selectedVehicle.technical_control_status.message || 'N/A'}
                              </Badge>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Chauffeurs assignés */}
                {selectedVehicle.assigned_drivers && selectedVehicle.assigned_drivers.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Chauffeurs assignés</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {selectedVehicle.assigned_drivers.map((driver) => (
                          <div key={driver.id} className="p-3 bg-muted/30 rounded-lg">
                            <p className="font-medium">{driver.name}</p>
                            <p className="text-sm text-muted-foreground">Téléphone: {driver.phone}</p>
                            <p className="text-sm text-muted-foreground">Permis: {driver.license_number}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Impossible de charger les détails du véhicule.</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modale pour les détails complets de l'offre */}
        <Dialog open={offerDetailsModalOpen} onOpenChange={setOfferDetailsModalOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Détails complets de l'offre
              </DialogTitle>
              <DialogDescription>
                Informations détaillées de l'offre et de la demande associée
              </DialogDescription>
            </DialogHeader>
            {selectedOfferForDetails ? (
              <div className="space-y-6">
                {(() => {
                  const offerRequest = selectedOfferForDetails.request_detail || selectedOfferForDetails.request || request;
                  const offerTransporter = selectedOfferForDetails.transporter_detail || selectedOfferForDetails.transporter;
                  const offerPriceDiff = calculatePriceDifference(selectedOfferForDetails.price, offerRequest?.declared_value);

                  return (
                    <>
                      {/* Vue d'ensemble */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Vue d'ensemble</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-muted-foreground">Statut</Label>
                              <div className="mt-1">{getOfferStatusBadge(selectedOfferForDetails.status)}</div>
                            </div>
                            <div>
                              <Label className="text-muted-foreground">Date de création</Label>
                              <p className="text-sm mt-1">{formatDate(selectedOfferForDetails.created_at)}</p>
                            </div>
                            {selectedOfferForDetails.logiconnect_id && (
                              <div className="col-span-2">
                                <Label className="text-muted-foreground">ID LogiConnect</Label>
                                <div className="mt-1">
                                  <LogiConnectIdBadge id={selectedOfferForDetails.logiconnect_id} showLabel={true} />
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Comparaison détaillée */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Package className="w-5 h-5 text-primary" />
                            Comparaison détaillée
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Demande du client */}
                            <div className="space-y-4 p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                              <div className="flex items-center gap-2 mb-3">
                                <User className="w-5 h-5 text-blue-500" />
                                <Label className="font-semibold">Demande du client</Label>
                              </div>
                              <div className="space-y-3">
                                <div>
                                  <Label className="text-muted-foreground text-xs">Prix proposé</Label>
                                  <p className="font-bold text-xl text-yellow-400 drop-shadow-lg mt-1">
                                    {offerRequest?.declared_value ? formatCurrency(offerRequest.declared_value) : 'Non spécifié'}
                                  </p>
                                </div>
                                {offerRequest && (
                                  <>
                                    <Separator />
                                    <div>
                                      <Label className="text-muted-foreground text-xs flex items-center gap-1">
                                        <MapPin className="w-4 h-4" />
                                        Itinéraire
                                      </Label>
                                      <p className="text-sm mt-1 font-medium">
                                        {offerRequest.from_location_detail?.city || offerRequest.from_location_detail?.label || offerRequest.from_location?.city || offerRequest.from_location?.label || 'N/A'} → {offerRequest.to_location_detail?.city || offerRequest.to_location_detail?.label || offerRequest.to_location?.city || offerRequest.to_location?.label || 'N/A'}
                                      </p>
                                      {(offerRequest.from_location_detail?.country || offerRequest.from_location?.country) && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                          {offerRequest.from_location_detail?.country || offerRequest.from_location?.country} → {offerRequest.to_location_detail?.country || offerRequest.to_location?.country || ''}
                                        </p>
                                      )}
                                    </div>
                                    <Separator />
                                    <div>
                                      <Label className="text-muted-foreground text-xs flex items-center gap-1">
                                        <Calendar className="w-4 h-4" />
                                        Dates préférées
                                      </Label>
                                      <p className="text-sm mt-1">
                                        Collecte: {offerRequest.preferred_pickup_date ? formatDate(offerRequest.preferred_pickup_date) : 'N/A'}
                                      </p>
                                      <p className="text-sm">
                                        Livraison: {offerRequest.preferred_delivery_date ? formatDate(offerRequest.preferred_delivery_date) : 'N/A'}
                                      </p>
                                    </div>
                                    <Separator />
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <Label className="text-muted-foreground text-xs">Mode de paiement</Label>
                                        <div className="mt-1">{offerRequest.payment_terms ? getPaymentTermsBadge(offerRequest.payment_terms) : 'N/A'}</div>
                                      </div>
                                      <div>
                                        <Label className="text-muted-foreground text-xs">Urgence</Label>
                                        <div className="mt-1">{offerRequest.urgency_level ? getUrgencyBadge(offerRequest.urgency_level) : 'N/A'}</div>
                                      </div>
                                    </div>
                                    {offerRequest.description && (
                                      <>
                                        <Separator />
                                        <div>
                                          <Label className="text-muted-foreground text-xs">Description</Label>
                                          <p className="text-sm mt-1">{offerRequest.description}</p>
                                        </div>
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Offre du transporteur */}
                            <div className="space-y-4 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                              <div className="flex items-center gap-2 mb-3">
                                <Truck className="w-5 h-5 text-green-500" />
                                <Label className="font-semibold">Offre du transporteur</Label>
                              </div>
                              <div className="space-y-3">
                                <div>
                                  <Label className="text-muted-foreground text-xs">Prix proposé</Label>
                                  <div className="flex items-center gap-2 mt-1">
                                    <p className="font-bold text-2xl text-secondary">
                                      {formatCurrency(selectedOfferForDetails.price)}
                                    </p>
                                    {offerPriceDiff && (
                                      <Badge className={offerPriceDiff.isIncrease ? 'bg-red-500/20 text-red-400 border-red-500/30' : offerPriceDiff.isDecrease ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-gray-500/20 text-gray-400 border-gray-500/30'}>
                                        {offerPriceDiff.isIncrease ? '↑' : offerPriceDiff.isDecrease ? '↓' : '='} {offerPriceDiff.percentage}%
                                      </Badge>
                                    )}
                                  </div>
                                  {offerPriceDiff && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {offerPriceDiff.isIncrease ? 'Augmentation de' : offerPriceDiff.isDecrease ? 'Réduction de' : 'Prix identique'} {offerPriceDiff.percentage}%
                                    </p>
                                  )}
                                </div>
                                {selectedOfferForDetails.admin_display_price && (
                                  <>
                                    <Separator />
                                    <div>
                                      <Label className="text-muted-foreground text-xs">Prix administrateur</Label>
                                      <p className="font-bold text-xl text-secondary mt-1">
                                        {formatCurrency(selectedOfferForDetails.admin_display_price)}
                                      </p>
                                      {selectedOfferForDetails.price && selectedOfferForDetails.admin_display_price !== selectedOfferForDetails.price && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                          Différence: {formatCurrency(Math.abs(selectedOfferForDetails.admin_display_price - selectedOfferForDetails.price))}
                                          {selectedOfferForDetails.admin_display_price > selectedOfferForDetails.price ? ' (augmentation)' : ' (réduction)'}
                                        </p>
                                      )}
                                    </div>
                                  </>
                                )}
                                <Separator />
                                {(() => {
                                  const vehicle = selectedOfferForDetails.vehicle_detail || selectedOfferForDetails.vehicle;
                                  const vehicleId = vehicle?.id || selectedOfferForDetails.vehicle;
                                  return vehicleId ? (
                                    <div>
                                      <Label className="text-muted-foreground text-xs flex items-center gap-1">
                                        <Truck className="w-4 h-4" />
                                        Véhicule proposé
                                      </Label>
                                      <div className="mt-1 space-y-1">
                                        <p className="text-sm font-medium">
                                          {vehicle?.plate_number || vehicle?.license_plate || 'N/A'} - {vehicle?.vehicle_type_label || vehicle?.vehicle_type?.label || vehicle?.vehicle_type?.name || 'N/A'}
                                        </p>
                                        {vehicle?.make && vehicle?.model && (
                                          <p className="text-xs text-muted-foreground">
                                            {vehicle.make} {vehicle.model} {vehicle.year ? `(${vehicle.year})` : ''}
                                          </p>
                                        )}
                                        {vehicle?.capacity_volume && (
                                          <p className="text-xs text-muted-foreground">
                                            Capacité: {vehicle.capacity_volume} m³
                                            {vehicle?.capacity_weight && ` / ${vehicle.capacity_weight} kg`}
                                          </p>
                                        )}
                                        <Button
                                          variant="default"
                                          size="sm"
                                          className="mt-2 text-xs"
                                          onClick={() => {
                                            setOfferDetailsModalOpen(false);
                                            handleViewVehicle(vehicleId);
                                          }}
                                        >
                                          <Eye className="w-3 h-3 mr-1" />
                                          Voir les détails complets du véhicule
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div>
                                      <Label className="text-muted-foreground text-xs flex items-center gap-1">
                                        <Truck className="w-4 h-4" />
                                        Véhicule
                                      </Label>
                                      <p className="text-sm mt-1 text-muted-foreground">Aucun véhicule assigné</p>
                                    </div>
                                  );
                                })()}
                                {selectedOfferForDetails.estimated_distance_km && (
                                  <>
                                    <Separator />
                                    <div>
                                      <Label className="text-muted-foreground text-xs">Distance estimée</Label>
                                      <p className="text-sm mt-1 font-medium">{selectedOfferForDetails.estimated_distance_km} km</p>
                                    </div>
                                  </>
                                )}
                                <Separator />
                                <div>
                                  <Label className="text-muted-foreground text-xs flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    Date de création
                                  </Label>
                                  <p className="text-sm mt-1">{formatDate(selectedOfferForDetails.created_at)}</p>
                                </div>
                                <Separator />
                                <div>
                                  <Label className="text-muted-foreground text-xs">Transporteur</Label>
                                  <p className="text-sm mt-1 font-medium">
                                    {getTransporterName(offerTransporter)}
                                  </p>
                                  {offerTransporter?.id && (
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0 h-auto text-xs mt-1"
                                      onClick={() => {
                                        setOfferDetailsModalOpen(false);
                                        handleViewTransporter(offerTransporter.id);
                                      }}
                                    >
                                      <Eye className="w-3 h-3 mr-1" />
                                      Voir le profil
                                    </Button>
                                  )}
                                </div>
                                {selectedOfferForDetails.message && (
                                  <>
                                    <Separator />
                                    <div>
                                      <Label className="text-muted-foreground text-xs">Message du transporteur</Label>
                                      <div className="mt-1 p-2 bg-muted/50 rounded text-sm">
                                        {selectedOfferForDetails.message}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Impossible de charger les détails de l'offre.</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog de gestion de visibilité de la demande */}
        <Dialog open={requestVisibilityDialogOpen} onOpenChange={setRequestVisibilityDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Gérer la visibilité de la demande</DialogTitle>
              <DialogDescription>
                Activez ou désactivez la visibilité de cette demande pour tous les transporteurs et définissez la commission de la plateforme.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              {/* Toggle visibilité */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label className="text-base font-semibold">Rendre visible aux transporteurs</Label>
                  <p className="text-sm text-muted-foreground">
                    Activez cette option pour que les transporteurs puissent voir cette demande
                  </p>
                </div>
                <Switch
                  checked={requestVisibilityData.visible_to_transporters}
                  onCheckedChange={(checked) => {
                    setRequestVisibilityData({
                      ...requestVisibilityData,
                      visible_to_transporters: checked,
                    });
                  }}
                />
              </div>

              {/* Prix déclaré par le client */}
              {request?.declared_value && (
                <div className="p-4 border rounded-lg bg-muted/50">
                  <Label className="text-sm font-semibold text-muted-foreground">Prix déclaré par le client</Label>
                  <p className="text-xl font-bold text-primary mt-1">
                    {formatCurrency(request.declared_value)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ce prix sera visible par les transporteurs
                  </p>
                </div>
              )}

              {!request?.declared_value && requestVisibilityData.visible_to_transporters && (
                <div className="p-4 border rounded-lg bg-yellow-500/10 border-yellow-500/30">
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">
                    ⚠️ Le client n'a pas défini de prix. Les transporteurs verront que le prix n'est pas connu.
                  </p>
                </div>
              )}

              {requestVisibilityData.visible_to_transporters && (
                <>
                  <Separator />

                  {/* Type de commission */}
                  <div className="space-y-2">
                    <Label htmlFor="commission_type">Type de commission</Label>
                    <Select
                      value={requestVisibilityData.commission_type}
                      onValueChange={(value) => {
                        setRequestVisibilityData({
                          ...requestVisibilityData,
                          commission_type: value,
                        });
                      }}
                    >
                      <SelectTrigger id="commission_type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PERCENTAGE">Pourcentage</SelectItem>
                        <SelectItem value="FIXED_AMOUNT">Montant fixe</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Choisissez comment calculer la commission de la plateforme
                    </p>
                  </div>

                  {/* Commission en pourcentage */}
                  {requestVisibilityData.commission_type === 'PERCENTAGE' && (
                    <div className="space-y-2">
                      <Label htmlFor="commission_percentage">Pourcentage de commission (%)</Label>
                      <Input
                        id="commission_percentage"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder={defaultCommissionPercentage ? `Par défaut: ${defaultCommissionPercentage}%` : 'Ex: 10.00 pour 10%'}
                        value={requestVisibilityData.commission_percentage}
                        onChange={(e) => setRequestVisibilityData({ ...requestVisibilityData, commission_percentage: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground">
                        {defaultCommissionPercentage && !requestVisibilityData.commission_percentage && (
                          <span>Valeur par défaut: {defaultCommissionPercentage}%</span>
                        )}
                        {!defaultCommissionPercentage && (
                          <span>Laissez vide pour ne pas appliquer de commission</span>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Commission en montant fixe */}
                  {requestVisibilityData.commission_type === 'FIXED_AMOUNT' && (
                    <div className="space-y-2">
                      <Label htmlFor="commission_fixed_amount">Montant fixe de commission (GNF)</Label>
                      <Input
                        id="commission_fixed_amount"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Ex: 50000"
                        value={requestVisibilityData.commission_fixed_amount}
                        onChange={(e) => setRequestVisibilityData({ ...requestVisibilityData, commission_fixed_amount: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground">
                        Montant fixe qui sera déduit du prix proposé par le transporteur
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRequestVisibilityDialogOpen(false)} disabled={updatingRequestVisibility}>
                Annuler
              </Button>
              <Button onClick={handleSaveRequestVisibility} disabled={updatingRequestVisibility}>
                {updatingRequestVisibility ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  'Enregistrer'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog pour afficher les paiements */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Paiements - Mission #{paymentBooking?.id?.slice(-8)}
              </DialogTitle>
            </DialogHeader>

            {paymentBooking && (
              <>
                <div className="mt-2 space-y-1 text-sm">
                  <div>Prix convenu: <span className="font-semibold text-primary">{formatCurrency(paymentBooking.agreed_price || paymentBooking.price)}</span></div>
                  <div>Total payé: <span className="font-semibold text-primary">{formatCurrency(paymentBooking.total_paid || 0)}</span> ({paymentBooking.payment_percentage || 0}%)</div>
                </div>

                <div className="mt-4 space-y-4">
                  {paymentBooking.payments && paymentBooking.payments.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Aucun paiement enregistré pour cette mission
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {paymentBooking.payments.map((payment) => {
                        const getPaymentStatusBadge = (status) => {
                          const statusConfig = {
                            'PENDING': { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
                            'SUCCESS': { color: 'bg-green-500/20 text-green-400 border-green-500/30' },
                            'FAILED': { color: 'bg-red-500/20 text-red-400 border-red-500/30' },
                            'REFUNDED': { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
                          };
                          const config = statusConfig[status] || { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' };
                          const label = constants?.payment_status?.find(s => s[0] === status)?.[1] || status;
                          return (
                            <Badge className={config.color}>
                              {label}
                            </Badge>
                          );
                        };
                        return (
                          <Card key={payment.id} className="bg-card/50 border-border">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2">
                                    <h4 className="font-semibold">Paiement #{payment.id?.slice(-8)}</h4>
                                    {getPaymentStatusBadge(payment.status)}
                                    <Badge variant="outline">
                                      {payment.method_code || payment.method || 'N/A'}
                                    </Badge>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div>
                                      <Label className="text-muted-foreground text-xs">Montant</Label>
                                      <p className="mt-1 font-semibold text-secondary">{formatCurrency(payment.amount)}</p>
                                    </div>
                                    <div>
                                      <Label className="text-muted-foreground text-xs">Date</Label>
                                      <p className="mt-1">{formatDate(payment.created_at)}</p>
                                    </div>
                                    <div>
                                      <Label className="text-muted-foreground text-xs">Référence</Label>
                                      <p className="mt-1 font-mono text-xs">{payment.provider_reference || 'N/A'}</p>
                                    </div>
                                    {payment.proof && (
                                      <div>
                                        <Label className="text-muted-foreground text-xs">Preuve</Label>
                                        <div className="mt-1">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => window.open(payment.proof, '_blank')}
                                          >
                                            <Eye className="w-3 h-3 mr-1" />
                                            Voir
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog pour créer un paiement au transporteur */}
        <Dialog open={createPaymentDialogOpen} onOpenChange={setCreatePaymentDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="pb-4">
              <DialogTitle className="flex items-center gap-3 text-2xl">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Wallet className="w-6 h-6 text-primary" />
                </div>
                <span>Payer le transporteur</span>
              </DialogTitle>
              {paymentBooking && (
                <Card className="mt-4 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Mission</p>
                        <p className="font-semibold text-sm">#{paymentBooking.id?.slice(-8)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Prix transporteur</p>
                        <p className="font-semibold text-sm text-secondary">{formatCurrency(paymentBooking.transporter_price || paymentBooking.agreed_price || paymentBooking.price)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Déjà payé</p>
                        <p className="font-semibold text-sm text-secondary">
                          {formatCurrency(
                            (paymentBooking.payments || [])
                              .filter(p => {
                                const transporter = paymentBooking.transporter_detail || paymentBooking.transporter;
                                const transporterUserId = transporter?.user;
                                return (p.payee_user === transporterUserId || p.payee_user?.id === transporterUserId) && p.status === 'SUCCESS';
                              })
                              .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Montant restant</p>
                        <p className="font-semibold text-sm text-orange-500">{formatCurrency(paymentData.amount || 0)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </DialogHeader>

            <div className="space-y-6 py-2">
              {/* Sélection de la méthode de paiement */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Méthode de paiement *</Label>
                <Select
                  value={paymentData.method_code}
                  onValueChange={(value) => setPaymentData({ ...paymentData, method_code: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une méthode" />
                  </SelectTrigger>
                  <SelectContent>
                    {constants?.payment_methods?.map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Montant */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Montant (GNF) *</Label>
                <Input
                  type="number"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                  placeholder="Montant à payer"
                />
              </div>

              {/* Champs conditionnels selon la méthode */}
              {(paymentData.method_code === 'ORANGE_MONEY' || paymentData.method_code === 'MTN_MOMO') && (
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Numéro de téléphone *</Label>
                  <Input
                    type="tel"
                    value={paymentData.phone_number}
                    onChange={(e) => setPaymentData({ ...paymentData, phone_number: e.target.value })}
                    placeholder="9 chiffres"
                  />
                </div>
              )}

              {paymentData.method_code === 'BANK_TRANSFER' && (
                <>
                  <div>
                    <Label className="text-sm font-semibold mb-2 block">Banque *</Label>
                    <Input
                      value={paymentData.bank_name}
                      onChange={(e) => setPaymentData({ ...paymentData, bank_name: e.target.value })}
                      placeholder="Nom de la banque"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold mb-2 block">Numéro de compte *</Label>
                    <Input
                      value={paymentData.account_number}
                      onChange={(e) => setPaymentData({ ...paymentData, account_number: e.target.value })}
                      placeholder="Numéro de compte"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold mb-2 block">Référence *</Label>
                    <Input
                      value={paymentData.reference}
                      onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
                      placeholder="Référence du virement"
                    />
                  </div>
                </>
              )}

              {paymentData.method_code === 'CASH' && (
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Preuve de paiement *</Label>
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setPaymentData({ ...paymentData, proof_file: e.target.files?.[0] || null })}
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreatePaymentDialogOpen(false)} disabled={creatingPayment}>
                Annuler
              </Button>
              <Button onClick={handleCreatePayment} disabled={creatingPayment}>
                {creatingPayment ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  'Créer le paiement'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    </>
  );
};

export default AdminRequestDetails;
